import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { task } from 'mobx-task'
import type { NotificationPreferencesStore } from '@/features/activity/NotificationPreferencesStore'
import { Subject } from 'rxjs'
import { action, computed, observable, runInAction } from 'mobx'
import {
  NotificationMediaPreferences,
  NotificationPreferences,
} from '@/features/activity/NotificationPreferences'
import { memoize } from '@/utils/memoize-util'
import type { SnackbarFactory } from '@/features/shell'

const SNACKBAR_KEY = Symbol('snackbar')

export class NotificationPreferencesViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private _preferences = NotificationPreferences.none
  private readonly _requestClose$ = new Subject<unknown>()

  /**
   * Emits when the view model is requesting the view to close.
   */
  readonly requestClose$ = this._requestClose$.asObservable()

  /**
   * The global preferences.
   */
  @observable accessor globalPreferences = new PreferenceItemViewModel(
    'All notifications',
    null,
    NotificationMediaPreferences.default(),
  )

  /**
   * Notification type specific preferences.
   */
  @observable accessor typeSpecificPreferences = new Map<
    string,
    PreferenceItemViewModel
  >()

  constructor(
    private readonly _preferencesStore: NotificationPreferencesStore,
    private readonly _newSnackbar: SnackbarFactory,
  ) {
    this.fetchPreferences = this.fetchPreferences.wrap((inner) =>
      memoize(inner, { promise: ([sig]) => sig }),
    )
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  fetchPreferences = task(async (signal: AbortSignal) => {
    const preferences = await this._preferencesStore.fetchPreferences(signal)
    signal.throwIfAborted()

    runInAction(() => {
      this._preferences = preferences

      this.globalPreferences = new PreferenceItemViewModel(
        'All notifications',
        null,
        preferences.media,
      )

      for (const type in notificationTypeContents) {
        const content = notificationTypeContents[type]
        const pref = preferences.types.get(type)
        this.typeSpecificPreferences.set(
          type,
          new PreferenceItemViewModel(
            content.title,
            content.description,
            pref ?? NotificationMediaPreferences.default(),
            () => this.globalPreferences,
          ),
        )
      }
    })
  })

  updatePreferences = task.resolved(async () => {
    // Initialize with existing preferences
    const types = new Map<string, NotificationMediaPreferences>(
      this._preferences.types,
    )

    this.typeSpecificPreferences.forEach((vm, type) => {
      types.set(type, {
        email: {
          enabled: vm.emailEnabled,
        },
        push: {
          enabled: vm.pushEnabled,
        },
      })
    })

    const snackbar = this._newSnackbar().withKey(SNACKBAR_KEY)
    await this._preferencesStore
      .updatePreferences({
        types,
        media: {
          email: {
            enabled: this.globalPreferences.emailEnabled,
          },
          push: {
            enabled: this.globalPreferences.pushEnabled,
          },
        },
      })
      .catch(snackbar.fromError)

    snackbar.update((s) =>
      s
        .type('success')
        .saying('Success!', 'Your notification preferences have been updated.')
        .autoDismiss()
        .show(),
    )
    this._requestClose$.next(undefined)
  })
}

export class PreferenceItemViewModel {
  @observable accessor emailEnabled
  @observable accessor pushEnabled

  constructor(
    public readonly title: string,
    public readonly description: string | null,
    preference: NotificationMediaPreferences,
    private readonly _getGlobalPreferences?: () => PreferenceItemViewModel,
  ) {
    this.emailEnabled = Boolean(preference.email?.enabled)
    this.pushEnabled = Boolean(preference.push?.enabled)
  }

  @computed
  get isEmailOverridden() {
    const pref = this._getGlobalPreferences?.()
    if (!pref) {
      // This *is* the global pref
      return false
    }

    return !pref.emailEnabled
  }

  @computed
  get isPushOverridden() {
    const pref = this._getGlobalPreferences?.()
    if (!pref) {
      // This *is* the global pref
      return false
    }

    return !pref.pushEnabled
  }

  @action.bound
  setEmailEnabled(enabled: boolean) {
    if (this.isEmailOverridden) {
      return
    }
    this.emailEnabled = enabled
  }

  @action.bound
  setPushEnabled(enabled: boolean) {
    if (this.isPushOverridden) {
      return
    }

    this.pushEnabled = enabled
  }
}

const notificationTypeContents: Record<
  string,
  { title: string; description: string }
> = {
  JobCancelled: {
    title: 'Job canceled',
    description: 'Receive notifications when a job has been canceled.',
  },
  JobClosed: {
    title: 'Job closed',
    description: 'Receive notifications when a job has been closed.',
  },
  JobReopened: {
    title: 'Job reopened',
    description: 'Receive notifications when a job has been reopened.',
  },
  JobUpdated: {
    title: 'Job updated',
    description: 'Receive notifications when a job has been updated.',
  },
  NewMessage: {
    title: 'New message',
    description: 'Receive notifications when a new message has been received.',
  },
  UnreadMessage: {
    title: 'Unread message',
    description: 'Receive notifications when a message has not been read.',
  },
  UnreadDocument: {
    title: 'Unread document',
    description: 'Receive notifications when you have an unread document.',
  },
}
